import { IDrawer } from "@theme/drawer/drawer";
import { IAccountOverlay } from "@snippets/account-overlay";
import { debounce } from "@helpers/utils";

class Header extends HTMLElement implements Header {
  private searchButton!: HTMLButtonElement | null;
  private menuButton!: HTMLButtonElement | null;
  private accountOverlayButton!: HTMLButtonElement | null;

  constructor() {
    super();
    this.setHeaderHeightVariable = this.setHeaderHeightVariable.bind(this);
  }

  connectedCallback() {
    this.cacheDomElements();
    this.setEventListeners();
    this.setHeaderHeightVariable();
  }

  private cacheDomElements() {
    this.searchButton = this.querySelector(
      "[js-header='mobile-search']",
    ) as HTMLButtonElement;

    this.menuButton = this.querySelector(
      '[js-header="hamburger"]',
    ) as HTMLButtonElement;

    this.accountOverlayButton = this.querySelector(
      '[js-header="accountOverlay"]',
    ) as HTMLButtonElement;

    // Error handling if critical elements are missing
    if (!this.searchButton || !this.menuButton) {
      console.error(
        "One or more critical elements are missing in the header component",
      );
    }
  }

  private setEventListeners() {
    const mobileMenu = this.querySelector(
      "uwp-drawer[modal-name='mobile-menu']",
    ) as IDrawer;

    const accountOverlay = this.querySelector(
      "uwp-account-overlay[modal-name='account-overlay']",
    ) as IAccountOverlay;

    this.menuButton?.addEventListener("click", () => {
      mobileMenu?.showDrawer();
    });

    this.searchButton?.addEventListener("click", () => {
      mobileMenu?.showDrawer();
    });

    this.accountOverlayButton?.addEventListener("click", () => {
      accountOverlay?.toggleAccountOverlay();
    });

    window.addEventListener(
      "resize",
      debounce(this.setHeaderHeightVariable, 250),
    );

    document.addEventListener(
      "scroll",
      debounce(this.setHeaderHeightVariable, 250),
    );

    document.addEventListener(
      "announcement-bar:updated",
      this.setHeaderHeightVariable,
    );
  }

  private setHeaderHeightVariable() {
    const announcementBar = document.querySelector(
      "uwp-announcement-bar",
    ) as HTMLDivElement;
    const secondaryAnnouncementBar = document.querySelector(
      ".secondary-announcement-bar",
    ) as HTMLDivElement;

    const mainHeaderHeight = this.isElementInViewport(this)
      ? this.getVisibleHeight(this)
      : 0;

    const announcementBarHeight =
      announcementBar && this.isElementInViewport(announcementBar)
        ? this.getVisibleHeight(announcementBar)
        : 0;

    const secondaryAnnouncementBarHeight =
      secondaryAnnouncementBar &&
      this.isElementInViewport(secondaryAnnouncementBar)
        ? this.getVisibleHeight(secondaryAnnouncementBar)
        : 0;

    const totalHeight =
      mainHeaderHeight + announcementBarHeight + secondaryAnnouncementBarHeight;

    document.documentElement.style.setProperty(
      "--header-height",
      `${totalHeight}px`,
    );
  }

  private isElementInViewport(element: HTMLElement): boolean {
    const rect = element.getBoundingClientRect();

    return (
      rect.top <
        (window.innerHeight || document.documentElement.clientHeight) &&
      rect.bottom > 0 &&
      rect.left < (window.innerWidth || document.documentElement.clientWidth) &&
      rect.right > 0
    );
  }

  private getVisibleHeight(element: HTMLElement): number {
    const rect = element.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;

    const visibleHeight = Math.max(
      0,
      Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0),
    );

    return visibleHeight;
  }
}

// Defining the custom element
customElements.define("uwp-header", Header);
